import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler } from '@angular/common/http';

/**
 * Attaches the JWT to every outgoing request to the backend.
 *
 * Why this exists:
 *   - Before this interceptor, each service manually read `localStorage.getItem('token')`
 *     and built its own `headers` object. That was duplicated in 5+ ECO services
 *     (`eco-clients`, `eco-orders`, `ticket`, `utils`, `customers/clients`) and
 *     was easy to forget on new calls.
 *   - Centralising the token attachment ensures every request to the API gets
 *     the latest token without service authors having to think about it.
 *
 * Excludes the login endpoint itself (no token yet) and any third-party URL
 * (the API base url lives in environment.base_url; anything else is forwarded
 * without an Authorization header).
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
    private static readonly baseUrl = '/back/';

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        // Don't attach a token to login/renew or to non-backend URLs.
        const isBackend = req.url.includes(AuthTokenInterceptor.baseUrl)
            || req.url.startsWith('/back/')
            || req.url.includes('/back/');
        if (!isBackend) {
            return next.handle(req);
        }
        // Login endpoint — no token yet.
        if (req.url.includes('/login')) {
            return next.handle(req);
        }

        const token = localStorage.getItem('token');
        if (!token) {
            return next.handle(req);
        }

        const cloned = req.clone({
            setHeaders: { 'x-token': token }
        });
        return next.handle(cloned);
    }
}
